import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Calendar, Shield, BarChart3, GraduationCap, Globe, ClipboardCheck,
  AlertTriangle, Bot, FileText, FolderKanban, FileSignature, GitMerge,
  ChevronDown, ChevronRight, Sparkles, Lock, Key,
  Building2, Search, Presentation, UserCheck, Users, Activity,
  FolderArchive, Brain, Bell, Gavel, Star, Clock, TrendingUp, Zap,
  Flame, CalendarCheck, Workflow, Router, Waves, Package, Award, ShieldCheck, Leaf,
  Target, Database, FileSearch, Send
} from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Navigation Structure (neutral colors - styling applied in component)
const NAVIGATION_SECTIONS = [
  {
    id: 'platform',
    name: 'Platform',
    items: [
      { name: 'Command Center', link: '/command-center', icon: Activity },
      { name: 'Unified Calendar', link: '/unified-calendar', icon: Calendar },
      { name: 'Document Vault', link: '/vault', icon: FolderArchive },
      { name: 'Global Search', link: '/search', icon: Search },
      { name: 'Quick Capture', link: '/quick-capture', icon: Sparkles },
    ]
  },
  {
    id: 'executive',
    name: 'Executive',
    items: [
      { name: 'Business Objectives', link: '/business-objectives', icon: Target, description: 'Strategic objectives with risk exposure' },
      { name: 'Risk Heatmap', link: '/executive/risk-heatmap', icon: Flame },
      { name: 'Compliance Score', link: '/compliance-score', icon: Award },
      { name: 'AI Insights', link: '/ai-insights', icon: Brain },
      { name: 'Regulation Ripple', link: '/regulatory-impact', icon: Waves },
      { name: 'Audit Pack Gen', link: '/audit-pack', icon: Package },
    ]
  },
  {
    id: 'tools',
    name: 'Tools',
    items: [
      { name: 'Workflow Engine', link: '/executive/workflow-management', icon: Workflow },
      { name: 'Document AI Router', link: '/executive/document-ai-router', icon: Router },
      { name: 'RFP Assistant', link: '/rfp-assistant', icon: FileText },
      { name: 'Automation', link: '/automation-workflows', icon: Zap },
    ]
  },
  {
    id: 'governance',
    name: 'Governance',
    isGroupHeader: true,
    items: []
  },
  {
    id: 'grc',
    name: 'GRC',
    moduleId: 'grc',
    parentGroup: 'governance',
    items: [
      { name: 'GRC Dashboard', link: '/grc-dashboard', icon: Shield },
      { name: 'Compliance Frameworks', link: '/frameworks', icon: ClipboardCheck },
      { name: 'Regulatory Intelligence', link: '/regulatory-intelligence', icon: Globe },
      { name: 'Data Protection Centre', link: '/data-protection', icon: UserCheck },
      { name: 'AI Governance Centre', link: '/ai-governance', icon: Bot },
      { name: 'Asset Register (IAR)', link: '/information-assets', icon: Database },
      { name: 'Impact Assessment', link: '/impact-assessment', icon: FileSearch },
      { name: 'Business Continuity', link: '/business-continuity', icon: ShieldCheck },
      { name: 'ESG & Sustainability', link: '/esg', icon: Leaf },
    ]
  },
  {
    id: 'risk',
    name: 'Risk',
    moduleId: 'grc',
    parentGroup: 'governance',
    items: [
      { name: 'Risk Dashboard', link: '/risk-register', icon: AlertTriangle },
      { name: 'Objectives Report', link: '/risk-objectives-report', icon: Target },
    ]
  },
  {
    id: 'qms',
    name: 'QMS',
    moduleId: 'grc',
    parentGroup: 'governance',
    items: [
      { name: 'Quality Dashboard', link: '/qms', icon: FileText },
      { name: 'Document Library', link: '/documents', icon: FileText },
      { name: 'Policy Builder', link: '/policy-builder', icon: Sparkles },
      { name: 'Board Packs', link: '/board-packs-enhanced', icon: Presentation },
    ]
  },
  {
    id: 'operations',
    name: 'Operations',
    isGroupHeader: true,
    items: []
  },
  {
    id: 'pmo',
    name: 'PMO',
    moduleId: 'pmo',
    parentGroup: 'operations',
    items: [
      { name: 'PMO Dashboard', link: '/pmo', icon: FolderKanban },
      { name: 'PMO Analytics', link: '/pmo/analytics', icon: BarChart3 },
    ]
  },
  {
    id: 'contracts',
    name: 'Contracts',
    moduleId: 'contracts',
    parentGroup: 'operations',
    items: [
      { name: 'Contract Dashboard', link: '/contracts', icon: FileSignature },
      { name: 'Contract Analytics', link: '/contracts/analytics', icon: BarChart3 },
    ]
  },
  {
    id: 'procurement',
    name: 'Procurement',
    moduleId: 'contracts',
    parentGroup: 'operations',
    items: [
      { name: 'Tenders Dashboard', link: '/tenders', icon: Gavel },
      { name: 'Supplier List (PSL)', link: '/psl', icon: Users },
      { name: 'Vendor Lifecycle', link: '/vendor-lifecycle', icon: Building2 },
      { name: 'Vendor Scorecard', link: '/vendor-scorecard', icon: Star },
      { name: 'Contract Renewals', link: '/contract-renewals', icon: Clock },
      { name: 'Savings Tracker', link: '/savings-tracker', icon: TrendingUp },
      { name: 'Analytics', link: '/procurement-analytics', icon: BarChart3 },
    ]
  },
  {
    id: 'structure',
    name: 'Structure',
    isGroupHeader: true,
    items: []
  },
  {
    id: 'cosec',
    name: 'Company Secretary',
    moduleId: 'cosec',
    parentGroup: 'structure',
    items: [
      { name: 'CoSec Dashboard', link: '/cosec', icon: Building2 },
      { name: 'Board Management', link: '/pmo/board-management', icon: Users },
      { name: 'COI Register', link: '/cosec/coi-register', icon: FileText },
      { name: 'Corporate Entities', link: '/cosec/entities', icon: Building2 },
      { name: 'Group Structure', link: '/cosec/structure', icon: GitMerge },
      { name: 'Filing Calendar', link: '/cosec/filings', icon: Calendar },
      { name: 'Officer Directory', link: '/cosec/officers', icon: Users },
      { name: 'Authority Matrix', link: '/authority', icon: Key },
    ]
  },
  {
    id: 'strategic',
    name: 'Strategic',
    isGroupHeader: true,
    items: []
  },
  {
    id: 'ma',
    name: 'M&A',
    moduleId: 'ma',
    parentGroup: 'strategic',
    items: [
      { name: 'M&A Dashboard', link: '/ma', icon: GitMerge },
      { name: 'M&A Analytics', link: '/ma/analytics', icon: BarChart3 },
    ]
  },
  {
    id: 'ma_plus',
    name: 'M&A Pro+',
    moduleId: 'ma_plus',  // Separate module - requires Pro+ tier
    parentGroup: 'strategic',
    optional: true,
    items: [
      { name: 'Deal Comparison', link: '/ma/pro/deal-comparison', icon: GitMerge, description: 'Access from deal details', isToolLink: true },
      { name: 'Employment Analyser', link: '/tools/employment-analyser', icon: Users, description: 'Standalone contract analysis' },
      { name: 'Contract Analyser', link: '/tools/contract-analyser', icon: FileText, description: 'Standalone contract analysis' },
    ]
  }
];

// Unlock Modal Component
const UnlockModal = ({ isOpen, onClose, moduleName, onUnlock }) => {
  const [loading, setLoading] = useState(false);
  
  if (!isOpen) return null;
  
  const handleUnlock = async () => {
    setLoading(true);
    await onUnlock();
    setLoading(false);
  };
  
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[#0a0f1a] border border-white/10 rounded-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="p-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Lock className="h-8 w-8 text-primary" />
          </div>
          <h3 className="text-xl font-semibold text-center text-white mb-2">Unlock {moduleName}</h3>
          <p className="text-slate-400 text-center text-sm mb-6">
            This module requires a subscription upgrade to access.
          </p>
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1 border-white/10 text-white hover:bg-white/5" onClick={onClose}>
              Cancel
            </Button>
            <Button className="flex-1" onClick={handleUnlock} disabled={loading}>
              {loading ? 'Processing...' : 'View Plans'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Navigation Section Component - Neutral with primary teal hover
const NavSection = ({ section, isExpanded, onToggle, unlockedModules, onUnlock, theme }) => {
  const location = useLocation();
  const isLocked = section.moduleId && !unlockedModules.includes(section.moduleId) && !unlockedModules.includes('all');
  const isOptional = section.optional;
  
  // Check if any item in this section is active
  const hasActiveItem = section.items.some(item => 
    location.pathname === item.link || location.pathname.startsWith(item.link + '/')
  );

  // Group header (no toggle, just a label)
  if (section.isGroupHeader) {
    return (
      <div className="mt-6 mb-2 px-3">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-white/30">
          {section.name}
        </span>
      </div>
    );
  }

  // Indented section (under a group header)
  const isIndented = section.parentGroup;

  return (
    <div className={`mb-1 ${isIndented ? 'ml-1' : ''}`}>
      {/* Section Header - Neutral with Executive Teal on active/hover */}
      <button
        onClick={onToggle}
        aria-expanded={isExpanded}
        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
          hasActiveItem 
            ? 'bg-[#2D7D6F]/15 text-[#2D7D6F]' 
            : 'text-white/80 hover:bg-[#2D7D6F]/10 hover:text-[#2D7D6F]'
        }`}
        data-testid={`nav-section-${section.id}`}
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          <span>{section.name}</span>
          {isOptional && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/60 font-medium">
              OPTIONAL
            </span>
          )}
          {isLocked && (
            <Lock className="h-3 w-3 text-white/40" />
          )}
        </div>
      </button>

      {/* Section Items - Neutral with Executive Teal active state */}
      {isExpanded && (
        <div className="ml-4 mt-1 space-y-0.5">
          {section.items.map((item) => {
            const Icon = item.icon;
            // Precise active state - exact match only, OR child route for dashboard pages
            // Avoid matching /ma for /ma/pro/* routes
            const isExactMatch = location.pathname === item.link;
            const isChildRoute = item.link !== '/' && 
              !item.isToolLink && 
              location.pathname.startsWith(item.link + '/') &&
              !location.pathname.startsWith(item.link + '/pro/'); // Don't match parent for /pro/ sub-routes
            const isActive = isExactMatch || isChildRoute;
            
            if (isLocked) {
              return (
                <button
                  key={item.name}
                  onClick={() => onUnlock(section.name, section.moduleId)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all duration-200 text-white/40 hover:bg-white/5 hover:text-white/60"
                >
                  <Icon className="h-4 w-4" />
                  <span className="truncate">{item.name}</span>
                  <Lock className="h-3 w-3 ml-auto" />
                </button>
              );
            }
            
            return (
              <Link
                key={item.name}
                to={item.link}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all duration-200 ${
                  isActive
                    ? 'bg-[#2D7D6F] text-white font-medium'
                    : 'text-white/70 hover:bg-[#2D7D6F]/10 hover:text-[#2D7D6F]'
                }`}
                data-testid={`nav-item-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="h-4 w-4" />
                <span className="truncate">{item.name}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Main Products Sidebar Component
const ProductsSidebar = ({ collapsed, onToggleCollapse, isMobile, onCloseMobile }) => {
  const { theme } = useTheme();
  const { token } = useAuth();
  const { activeModules, lockedModules, trialStatus, isOnTrial, trialDaysRemaining } = useSubscription();
  const location = useLocation();
  
  // Start with all sections collapsed - will expand active one via useEffect
  const [expandedSections, setExpandedSections] = useState([]);
  const [unlockedModules, setUnlockedModules] = useState([]);
  const [unlockModal, setUnlockModal] = useState({ open: false, moduleName: '', moduleId: '' });

  // Auto-expand ONLY the section containing current route (collapse others on nav)
  useEffect(() => {
    const currentSection = NAVIGATION_SECTIONS.find(section =>
      section.items.some(item => 
        location.pathname === item.link || location.pathname.startsWith(item.link + '/')
      )
    );
    if (currentSection) {
      // Set only the current section as expanded (collapses others)
      setExpandedSections([currentSection.id]);
    }
  }, [location.pathname]);

  // Use active modules from subscription context (new trial system)
  useEffect(() => {
    if (activeModules && activeModules.length > 0) {
      // Map billing modules to nav section module IDs
      const moduleMapping = {
        'grc': ['grc', 'risk', 'qms', 'essentials', 'platform'],
        'cosec': ['cosec'],
        'contracts': ['contracts', 'vendor'],
        'pmo': ['pmo'],
        'ma': ['ma'],
        'ma_plus': ['ma_plus']  // M&A Pro+ module
      };
      
      const unlocked = [];
      activeModules.forEach(mod => {
        const mapped = moduleMapping[mod];
        if (mapped) {
          unlocked.push(...mapped);
        }
        unlocked.push(mod);
      });
      
      setUnlockedModules([...new Set(unlocked)]);
    } else {
      // Fallback to API fetch
      const fetchModules = async () => {
        if (!token) return;
        try {
          const response = await axios.get(`${API}/modules/entitlements`, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          });
          if (response.data.modules) {
            const moduleIds = Object.keys(response.data.modules);
            setUnlockedModules(moduleIds);
          }
        } catch (error) {
          setUnlockedModules(['essentials', 'platform']);
        }
      };
      fetchModules();
    }
  }, [token, activeModules]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleUnlock = (moduleName, moduleId) => {
    setUnlockModal({ open: true, moduleName, moduleId });
  };

  const handleUnlockConfirm = async () => {
    // Open pricing page for the specific module
    const moduleId = unlockModal.moduleId;
    if (moduleId) {
      window.location.href = `/pricing?module=${moduleId}`;
    } else {
      window.location.href = '/pricing';
    }
  };

  // Collapsed state
  if (collapsed) {
    return (
      <div className="w-12 flex-shrink-0 border-r border-white/[0.08] bg-[#020408]">
        <div className="p-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleCollapse}
            className="w-full h-8 text-white/60 hover:text-[#2D7D6F] hover:bg-[#2D7D6F]/10"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Sidebar - Same background as site (#020408) */}
      <div 
        className={`
          ${isMobile ? 'w-72 h-full pt-14' : 'w-64'} 
          flex-shrink-0 border-r border-white/[0.08] bg-[#020408] overflow-y-auto
        `}
        data-testid="products-sidebar"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 px-4 py-3 border-b border-white/[0.08] bg-[#020408]/95 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            {isMobile && (
              <span className="text-sm font-medium text-white/70">Navigation</span>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={isMobile ? onCloseMobile : onToggleCollapse}
              className="h-6 w-6 p-0 text-white/40 hover:text-[#2D7D6F] hover:bg-[#2D7D6F]/10 ml-auto"
            >
              {isMobile ? (
                <ChevronDown className="h-4 w-4 -rotate-90" />
              ) : (
                <ChevronDown className="h-4 w-4 rotate-90" />
              )}
            </Button>
          </div>
        </div>

        {/* Navigation Sections */}
        <div className="p-2">
          {NAVIGATION_SECTIONS.map((section) => (
            <NavSection
              key={section.id}
              section={section}
              isExpanded={expandedSections.includes(section.id)}
              onToggle={() => toggleSection(section.id)}
              unlockedModules={unlockedModules}
              onUnlock={handleUnlock}
              theme={theme}
            />
          ))}
        </div>
      </div>

      {/* Unlock Modal */}
      <UnlockModal
        isOpen={unlockModal.open}
        onClose={() => setUnlockModal({ open: false, moduleName: '' })}
        moduleName={unlockModal.moduleName}
        onUnlock={handleUnlockConfirm}
      />
    </>
  );
};

export default ProductsSidebar;
